import React,{
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {useAuth} from '../context/AuthContext.jsx';
import * as api from '../lib/api.js';
import './CreateModes.css';

const {
  FiArrowRight,
  FiCheck,
  FiCommand,
  FiImage,
  FiSparkles,
  FiUploadCloud,
  FiX
}=FiIcons;

const baseModes=[
  {id:'prompt',label:'Prompt'},
  {id:'pasted_text',label:'Paste text'},
  {id:'screenshot',label:'Upload screenshot'}
];

const fullCreativeMode={id:'full_creative',label:'Full Creative Mode'};
const CREATIVE_CANVAS_SIZE=1080;

function CreateScreen({
  brand,
  generating,
  error,
  onGenerate,
  selectedBackground,
  onClearBackground
}) {
  const {currentUser}=useAuth();
  const isVip=currentUser?.is_vip === true;
  const modes=useMemo(
    ()=> (isVip ? [...baseModes,fullCreativeMode] : baseModes),
    [isVip]
  );

  const [mode,setMode]=useState('prompt');
  const [prompt,setPrompt]=useState('');
  const [pastedText,setPastedText]=useState('');
  const [screenshot,setScreenshot]=useState(null);
  const [creativeMode,setCreativeMode]=useState('ai_creative');
  const [creativePrompt,setCreativePrompt]=useState('');
  const [creativeGenerating,setCreativeGenerating]=useState(false);
  const [creativeError,setCreativeError]=useState(null);
  const [creativeResult,setCreativeResult]=useState(null);
  const [guidedMessages,setGuidedMessages]=useState([]);
  const [guidedQuestion,setGuidedQuestion]=useState(null);
  const [guidedBrief,setGuidedBrief]=useState(null);
  const [guidedThinking,setGuidedThinking]=useState(false);
  const [guidedOther,setGuidedOther]=useState('');
  const [guidedSeed,setGuidedSeed]=useState('');
  const guidedBusyRef=useRef(false);

  useEffect(()=> {
    return ()=> {
      if (screenshot?.previewUrl) {
        URL.revokeObjectURL(screenshot.previewUrl);
      }
    };
  },[screenshot]);

  const handleFile=(event)=> {
    const file=event.target.files?.[0];

    if (!file || !file.type.startsWith('image/')) {
      return;
    }

    if (screenshot?.previewUrl) {
      URL.revokeObjectURL(screenshot.previewUrl);
    }

    setScreenshot({
      name:file.name,
      previewUrl:URL.createObjectURL(file),
      file
    });
  };

  const getSeed=()=> {
    if (mode==='prompt') {
      return prompt.trim()
        ? {prompt:prompt.trim(),seedContent:prompt.trim()}
        : null;
    }

    if (mode==='pasted_text') {
      const content=pastedText.trim();

      if (!content) {
        return null;
      }

      return {
        prompt:content.slice(0,120),
        seedContent:content
      };
    }

    if (!screenshot) {
      return null;
    }

    return {
      prompt:'From uploaded screenshot',
      seedContent:screenshot.previewUrl
    };
  };

  const submit=(event)=> {
    event.preventDefault();
    const seed=getSeed();

    if (seed && !generating) {
      onGenerate({
        ...seed,
        inputMode:mode,
        backgroundImage:selectedBackground?.imageUrl || null,
        screenshotFile:mode==='screenshot' ? screenshot?.file : null
      });
    }
  };

  const canSubmit=Boolean(getSeed()) && !generating;
  const funnelTurnActive=guidedThinking || Boolean(guidedQuestion);
  const lockCreativePrompt=creativeMode==='ai_creative' &&
    (funnelTurnActive || creativeGenerating);
  const showCreativeGenerate=creativeMode==='coding_creative' || !funnelTurnActive;
  const canSubmitCreative=creativePrompt.trim().length > 0 &&
    !creativeGenerating &&
    !(creativeMode==='ai_creative' && guidedThinking);

  const runGuidedGeneration=async (brief)=> {
    setCreativeGenerating(true);
    setCreativeResult(null);

    const {data,error}=await api.posts.generateGuidedCreative({
      brandId:brand.id,
      brief
    });

    setCreativeGenerating(false);

    if (error) {
      setCreativeError(error.message || String(error));
      return;
    }

    setCreativeResult(data);
  };

  const requestGuidedTurn=async (messages,seedOverride)=> {
    const seedContent=(seedOverride ?? guidedSeed).trim();

    setGuidedThinking(true);
    setCreativeError(null);

    try {
      const body={
        brandId:brand.id,
        seedContent
      };

      if (messages.length) {
        body.messages=messages;
      }

      const {data,error}=await api.posts.guidedCreativeChat(body);

      if (error) {
        setCreativeError(error.message || String(error));
        return;
      }

      if (data?.type==='question') {
        if (typeof data.question !== 'string' || !Array.isArray(data.options)) {
          setCreativeError('Unexpected response from guided creative chat.');
          return;
        }

        setGuidedQuestion({
          question:data.question,
          options:data.options.filter((option)=> typeof option === 'string')
        });
        return;
      }

      if (data?.type==='brief') {
        if (!data.brief || typeof data.brief !== 'object') {
          setCreativeError('Unexpected response from guided creative chat.');
          return;
        }

        setGuidedBrief(data.brief);
        setGuidedThinking(false);
        await runGuidedGeneration(data.brief);
        return;
      }

      setCreativeError('Unexpected response from guided creative chat.');
    } finally {
      setGuidedThinking(false);
      guidedBusyRef.current=false;
    }
  };

  const startGuidedFunnel=async ()=> {
    const seedContent=creativePrompt.trim();

    if (
      !seedContent ||
      creativeGenerating ||
      guidedThinking ||
      guidedBusyRef.current
    ) {
      return;
    }

    guidedBusyRef.current=true;
    setCreativeError(null);
    setCreativeResult(null);
    setGuidedMessages([]);
    setGuidedQuestion(null);
    setGuidedBrief(null);
    setGuidedOther('');
    setGuidedSeed(seedContent);
    await requestGuidedTurn([],seedContent);
  };

  const submitGuidedAnswer=async (answer)=> {
    const trimmed=answer.trim();

    if (
      !trimmed ||
      !guidedQuestion ||
      guidedThinking ||
      creativeGenerating ||
      guidedBusyRef.current
    ) {
      return;
    }

    guidedBusyRef.current=true;

    const nextMessages=[
      ...guidedMessages,
      {
        role:'assistant',
        content:JSON.stringify({
          question:guidedQuestion.question,
          options:guidedQuestion.options
        })
      },
      {role:'user',content:trimmed}
    ];

    setGuidedMessages(nextMessages);
    setGuidedOther('');
    setGuidedQuestion(null);
    await requestGuidedTurn(nextMessages);
  };

  const generateCodingCreative=async ()=> {
    const seedContent=creativePrompt.trim();

    if (!seedContent || creativeGenerating) {
      return;
    }

    setCreativeError(null);
    setCreativeGenerating(true);
    setCreativeResult(null);

    const {data,error}=await api.posts.generateCreativePost({
      brandId:brand.id,
      mode:'coding_creative',
      seedType:'prompt',
      seedContent
    });

    setCreativeGenerating(false);

    if (error) {
      setCreativeError(error.message || String(error));
      return;
    }

    setCreativeResult(data);
  };

  const generateCreative=()=> {
    if (creativeMode==='ai_creative') {
      return startGuidedFunnel();
    }

    return generateCodingCreative();
  };

  return (
    <motion.main
      className="page create-page"
      initial={{opacity:0}}
      animate={{opacity:1}}
    >
      <div className="page-kicker">
        <SafeIcon icon={FiSparkles} />
        Creative studio
      </div>

      <h1>
        What should we<br />
        <em>create today?</em>
      </h1>

      <p className="intro">
        Share one thought. DalePost will turn it into a polished, on-brand
        post for <strong>{brand.name}</strong>.
      </p>

      {error && (
        <p className="create-error" role="alert">{error}</p>
      )}

      {selectedBackground && (
        <div className="selected-background">
          <img src={selectedBackground.imageUrl} alt="" />
          <div>
            <span>Background selected</span>
            <strong>{selectedBackground.label}</strong>
          </div>
          <button
            type="button"
            aria-label="Clear selected background"
            onClick={onClearBackground}
          >
            <SafeIcon icon={FiX} />
          </button>
        </div>
      )}

      <div
        className="creation-modes"
        role="tablist"
        aria-label="Post input type"
      >
        {modes.map((item)=> (
          <button
            key={item.id}
            className={mode===item.id ? 'active' : ''}
            role="tab"
            aria-selected={mode===item.id}
            onClick={()=> setMode(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <form className="prompt-card" onSubmit={submit}>
        {mode==='prompt' && (
          <>
            <label htmlFor="post-prompt">Your one-line idea</label>
            <textarea
              id="post-prompt"
              value={prompt}
              onChange={(event)=> setPrompt(event.target.value)}
              placeholder="e.g. Create a post about why having a financial professional matters"
              maxLength={180}
              autoFocus
            />
            <div className="prompt-footer">
              <div className="prompt-meta">
                <SafeIcon icon={FiCommand} />
                <span>{prompt.length}/180</span>
              </div>
              <GenerateButton
                brand={brand}
                generating={generating}
                disabled={!canSubmit}
              />
            </div>
          </>
        )}

        {mode==='pasted_text' && (
          <>
            <label htmlFor="pasted-text">Paste your raw material</label>
            <textarea
              id="pasted-text"
              className="large-seed-input"
              value={pastedText}
              onChange={(event)=> setPastedText(event.target.value)}
              placeholder="“A client told me they finally feel confident looking at their finances…”"
              maxLength={800}
              autoFocus
            />
            <div className="prompt-footer">
              <div className="prompt-meta">
                <span>{pastedText.length}/800</span>
              </div>
              <GenerateButton
                brand={brand}
                generating={generating}
                disabled={!canSubmit}
              />
            </div>
          </>
        )}

        {mode==='full_creative' && (
          <>
            <div
              className="creation-modes creative-sub-modes"
              role="tablist"
              aria-label="Creative generation mode"
            >
              <button
                type="button"
                className={creativeMode==='ai_creative' ? 'active' : ''}
                role="tab"
                aria-selected={creativeMode==='ai_creative'}
                onClick={()=> setCreativeMode('ai_creative')}
              >
                AI Creative
              </button>
              <button
                type="button"
                className={creativeMode==='coding_creative' ? 'active' : ''}
                role="tab"
                aria-selected={creativeMode==='coding_creative'}
                onClick={()=> setCreativeMode('coding_creative')}
              >
                Coding Creative
              </button>
            </div>

            <label htmlFor="creative-prompt">Your one-line idea</label>
            <textarea
              id="creative-prompt"
              value={creativePrompt}
              onChange={(event)=> setCreativePrompt(event.target.value)}
              placeholder="e.g. Create a post about why having a financial professional matters"
              maxLength={180}
              disabled={lockCreativePrompt}
              autoFocus
            />
            <div className="prompt-footer">
              <div className="prompt-meta">
                <SafeIcon icon={FiCommand} />
                <span>{creativePrompt.length}/180</span>
              </div>
              {showCreativeGenerate && (
                <button
                  className="primary-button"
                  type="button"
                  disabled={!canSubmitCreative}
                  style={{background:brand.primary}}
                  onClick={generateCreative}
                >
                  {creativeGenerating ? (
                    <>
                      <span className="spinner" />
                      Generating
                    </>
                  ) : (
                    <>
                      Generate
                      <SafeIcon icon={FiArrowRight} />
                    </>
                  )}
                </button>
              )}
            </div>

            {creativeMode==='ai_creative' && (guidedThinking || guidedQuestion) && (
              <GuidedCreativeFunnel
                thinking={guidedThinking}
                question={guidedQuestion}
                otherValue={guidedOther}
                onOtherChange={setGuidedOther}
                onSelectOption={submitGuidedAnswer}
                onSubmitOther={()=> submitGuidedAnswer(guidedOther)}
              />
            )}

            {creativeError && (
              <p className="create-error creative-error" role="alert">
                {creativeError}
              </p>
            )}

            {creativeResult && (
              <div className="creative-preview">
                {creativeResult.headline != null && (
                  <p><strong>Headline:</strong> {creativeResult.headline}</p>
                )}
                {creativeResult.subhead != null && (
                  <p><strong>Subhead:</strong> {creativeResult.subhead}</p>
                )}
                {creativeResult.html && (
                  <CreativePreviewFrame html={creativeResult.html} />
                )}
              </div>
            )}
          </>
        )}

        {mode==='screenshot' && (
          <>
            <label htmlFor="screenshot-upload">
              Upload your source image
            </label>
            <label
              className={`upload-dropzone ${screenshot ? 'has-file' : ''}`}
              htmlFor="screenshot-upload"
            >
              {screenshot ? (
                <>
                  <img
                    src={screenshot.previewUrl}
                    alt="Uploaded source preview"
                  />
                  <span className="upload-success">
                    <SafeIcon icon={FiCheck} />
                    {screenshot.name}
                  </span>
                  <small>Click to choose a different image</small>
                </>
              ) : (
                <>
                  <span className="upload-icon">
                    <SafeIcon icon={FiUploadCloud} />
                  </span>
                  <strong>Drop a screenshot here</strong>
                  <small>or click to browse · PNG, JPG up to 10MB</small>
                </>
              )}
              <input
                id="screenshot-upload"
                type="file"
                accept="image/*"
                onChange={handleFile}
              />
            </label>
            <div className="prompt-footer">
              <div className="prompt-meta">
                <SafeIcon icon={FiImage} />
                <span>
                  {screenshot ? 'Image ready to read' : 'Mock image reading'}
                </span>
              </div>
              <GenerateButton
                brand={brand}
                generating={generating}
                disabled={!canSubmit}
              />
            </div>
          </>
        )}
      </form>

      {mode==='prompt' && (
        <div className="suggestions">
          <span>Try an idea</span>
          {[
            'Why consistency matters',
            'Planning for business growth',
            'Turn uncertainty into action'
          ].map((idea)=> (
            <button key={idea} onClick={()=> setPrompt(idea)}>
              {idea}
            </button>
          ))}
        </div>
      )}
    </motion.main>
  );
}

function GuidedCreativeFunnel({
  thinking,
  question,
  otherValue,
  onOtherChange,
  onSelectOption,
  onSubmitOther
}) {
  return (
    <div className="guided-funnel" aria-busy={thinking}>
      {thinking && (
        <div className="guided-thinking" aria-live="polite">
          <span className="guided-thinking-dots" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          Thinking
        </div>
      )}

      {!thinking && question && (
        <motion.div
          initial={{opacity:0,y:6}}
          animate={{opacity:1,y:0}}
          transition={{duration:0.22}}
        >
          <p className="guided-question">{question.question}</p>
          <div className="guided-options">
            {question.options.map((option,index)=> (
              <button
                key={`${index}-${option}`}
                type="button"
                className="guided-option"
                onClick={()=> onSelectOption(option)}
              >
                {option}
              </button>
            ))}
          </div>
          <div className="guided-other">
            <input
              type="text"
              value={otherValue}
              onChange={(event)=> onOtherChange(event.target.value)}
              onKeyDown={(event)=> {
                if (event.key==='Enter') {
                  event.preventDefault();
                  onSubmitOther();
                }
              }}
              placeholder="Something else…"
              aria-label="Something else"
            />
            <button
              className="secondary-button"
              type="button"
              disabled={!otherValue.trim()}
              onClick={onSubmitOther}
            >
              Use this
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}

function CreativePreviewFrame({html}) {
  const wrapRef=useRef(null);
  const [scale,setScale]=useState(1);

  useLayoutEffect(()=> {
    const node=wrapRef.current;

    if (!node) {
      return;
    }

    const updateScale=()=> {
      const width=node.clientWidth;

      if (width > 0) {
        setScale(width / CREATIVE_CANVAS_SIZE);
      }
    };

    updateScale();

    const observer=new ResizeObserver(updateScale);
    observer.observe(node);

    return ()=> observer.disconnect();
  },[]);

  const scaledHeight=CREATIVE_CANVAS_SIZE * scale;

  return (
    <div
      ref={wrapRef}
      className="creative-preview-frame-wrap"
      style={{height:scaledHeight}}
    >
      <div
        className="creative-preview-scale"
        style={{
          width:CREATIVE_CANVAS_SIZE,
          height:CREATIVE_CANVAS_SIZE,
          transform:`scale(${scale})`,
          transformOrigin:'top left'
        }}
      >
        <iframe
          className="creative-preview-frame"
          sandbox=""
          scrolling="no"
          title="Generated creative preview"
          srcDoc={html}
          width={CREATIVE_CANVAS_SIZE}
          height={CREATIVE_CANVAS_SIZE}
        />
      </div>
    </div>
  );
}

function GenerateButton({brand,generating,disabled}) {
  return (
    <button
      className="primary-button"
      type="submit"
      disabled={disabled}
      style={{background:brand.primary}}
    >
      {generating ? (
        <>
          <span className="spinner" />
          Designing your post
        </>
      ) : (
        <>
          Generate post
          <SafeIcon icon={FiArrowRight} />
        </>
      )}
    </button>
  );
}

export default CreateScreen;